import { Color, SRGBColorSpace } from 'three';
import type { SettingId } from '../track/setting';
import { mix, PAL } from './palette';

export { isSettingId, SETTING_IDS, type SettingId } from '../track/setting';

/**
 * The day's setting: where in Italy the circuit is. Only what the world looks like (sky and light, the
 * ground's colours, the hills behind, the trees and houses, a sea): never the road, the car or anything the
 * race runs on, so laps, ghosts and boards don't care which one a day gets. Tuscany is the game as it
 * always looked.
 */

/** What a planned tree becomes in a setting (the plan's kinds are Tuscany's). */
export type TreeKind = 'cypress' | 'pine' | 'olive' | 'bush' | 'spruce' | 'larch' | 'palm' | 'cactus' | 'oleander' | 'citrus';

/** One silhouette ring of the backdrop, far to near. */
export interface HillLayer {
  radius: number;
  /** Lowest top (m) and how much higher the tops go. */
  base: number;
  amp: number;
  color: Color;
  /** How much of the sky's horizon haze it takes on (0 to 1). */
  haze: number;
  /** 'round': rolling hills; 'jagged': mountain peaks. */
  shape: 'round' | 'jagged';
  /** Snow on the tops: its colour, and the share of `amp` it starts above. */
  snow?: { color: Color; above: number };
  /** A height factor round the ring (0 to 1), by angle: where the land is (none over a sea). */
  arc?: (a: number) => number;
  /** Extra height by angle (a volcano), m. */
  bump?: (a: number) => number;
  segments?: number;
}

/** Where the day's sea is, worked out from the circuit (the town's side stays land). */
export interface SeaPlan {
  /** Sim-space unit vector from the circuit out to sea. */
  dx: number;
  dy: number;
  /** The coast's distance from the circuit's centre along it, m (headlands and bays add to it). */
  coast: number;
  cx: number;
  cy: number;
  /**
   * The water's height: the setting's own, or below the circuit's lowest road on a hilly day (a fixed level
   * flooded the road wherever it dipped below it: Anello di San Fruttuoso, 8 October 2026).
   */
  level: number;
}

export interface Look {
  id: SettingId;
  name: string;
  sky: { horizon: Color; low: Color; mid: Color; high: Color; top: Color; sun: Color; halo: Color };
  light: {
    sunElevationDeg: number;
    /**
     * The shadow-casting light's height. Never under Tuscany's 21°: a lower light reaches further across the
     * ground, pulling more scenery into the shadow pass (more to draw). A low painted sun is fine.
     */
    lightElevationDeg: number;
    sun: number;
    hemi: number;
    sunColor: string;
    skyColor: string;
    groundColor: string;
    fogNear: number;
    fogFar: number;
  };
  ground: { grass: Color; fieldA: Color; fieldB: Color; fieldC: Color; shares?: [number, number, number] };
  trees: { cypress: Color; canopy: Color; leaf: Color; leafDark: Color; bush: Color; bushDark: Color; trunk: Color };
  houses: { walls: Color[]; farm: [Color, Color]; roof: Color; shutter: Color; door: Color; tower: Color; towerTop: Color; stone: Color; roofPitch: number };
  /**
   * What each of the plan's tree kinds becomes (by variant, cycling), and the far trees' mix. Kept no heavier
   * than Tuscany's: every stand-in has no more triangles than the tree it replaces (a palm for a cypress, a
   * lemon for an olive, a prickly pear for a bush), and the far mix averages no more per tree.
   */
  flora: Record<'cypress' | 'pine' | 'olive' | 'bush', TreeKind[]>;
  farTrees: [TreeKind, number][];
  /** Trees round the hilltop town. */
  townTrees: TreeKind[];
  /** Hay bales stay hay bales, or become log piles, or nothing. */
  bales: 'bale' | 'logs' | 'none';
  /** The low walls along the road: dry stone in these three colours, or wooden fences instead. */
  walls: { kind: 'stone' | 'fence'; stones: [Color, Color, Color] };
  /** Fences, the grandstand's frame. */
  wood: Color;
  /** The grandstand's colours: its awning's stripes and the front banner's three. */
  stand: { stripeA: Color; stripeB: Color; banner: [Color, Color, Color] };
  /** The hilltop town's tower: Tuscany's with merlons, Genoa's striped bell tower, an alpine spire, a baroque dome. */
  tower: 'tuscan' | 'striped' | 'spire' | 'dome';
  hills: (o: { seed: number; sunYaw: number; sea: SeaPlan | null }) => HillLayer[];
  /** A sea on one side (the coast, boats, a harbour light). */
  sea?: { color: Color; shallow: Color; level: number };
  /** A volcano on the skyline, smoking (Etna): its colour, height and where it stands relative to the sun. */
  volcano?: { color: Color; smoke: Color; height: number; yawFromSun: number };
}

const hex = (h: string): Color => new Color(h);
/** Two colours mixed as a painter would (sRGB), like palette.mix. */
export function mixC(a: Color, b: Color, t: number): Color {
  const A = a.getRGB({ r: 0, g: 0, b: 0 }, SRGBColorSpace);
  const B = b.getRGB({ r: 0, g: 0, b: 0 }, SRGBColorSpace);
  return new Color().setRGB(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t, SRGBColorSpace);
}

/** The angle round the backdrop's rings for a sim-space direction (rings run (cos a, sin a) in three's x, z). */
export const ringAngle = (dx: number, dy: number): number => Math.atan2(-dy, dx);

/** Smoothly 0 below e0 to 1 above e1. */
const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

/** Land round the ring except a sea's arc (facing `seaAngle`), the headlands either side tapering into it. */
const landArc = (sea: SeaPlan | null, half: number, taper: number) => (a: number): number => {
  if (!sea) return 1;
  const s = ringAngle(sea.dx, sea.dy);
  const d = Math.abs(Math.atan2(Math.sin(a - s), Math.cos(a - s)));
  return smoothstep(half, half + taper, d);
};

const TUSCAN_HORIZON = mix('ochre', 'cream', 0.24);

const TUSCANY: Look = {
  id: 'tuscany',
  name: 'Tuscany',
  sky: {
    horizon: TUSCAN_HORIZON,
    low: mix('ochre', 'cream', 0.06),
    mid: PAL.ochre.clone(),
    high: mix('ochre', 'terracotta', 0.2),
    top: mix('ochre', 'terracotta', 0.34),
    sun: PAL.cream.clone(),
    halo: mix('ochre', 'cream', 0.55),
  },
  light: { sunElevationDeg: 17, lightElevationDeg: 21, sun: 0.74, hemi: 0.5, sunColor: '#ffe2b2', skyColor: '#ffe9c8', groundColor: '#e39a62', fogNear: 260, fogFar: 3600 },
  ground: { grass: PAL.grass.clone(), fieldA: mix('grass', 'olive', 0.45), fieldB: mix('grass', 'terracotta', 0.3), fieldC: mix('grass', 'cream', 0.22) },
  trees: {
    cypress: PAL.cypress.clone(),
    canopy: mix('cypress', 'olive', 0.18),
    leaf: mix('olive', 'cypress', 0.08),
    leafDark: mix('olive', 'cypress', 0.25),
    bush: mix('olive', 'cypress', 0.15),
    bushDark: mix('cypress', 'olive', 0.4),
    trunk: mix('brown', 'terracotta', 0.35),
  },
  houses: {
    walls: [mix('cream', 'grass', 0.14), mix('cream', 'ochre', 0.3), mix('cream', 'terracotta', 0.18), mix('cream', 'asphalt', 0.12)],
    farm: [mix('cream', 'grass', 0.14), mix('cream', 'ochre', 0.3)],
    roof: PAL.terracotta.clone(),
    shutter: PAL.cypress.clone(),
    door: mix('cypress', 'brown', 0.3),
    tower: mix('cream', 'asphalt', 0.18),
    towerTop: mix('cream', 'asphalt', 0.25),
    stone: mix('asphalt', 'cream', 0.3),
    roofPitch: 0.28,
  },
  flora: { cypress: ['cypress'], pine: ['pine'], olive: ['olive'], bush: ['bush'] },
  farTrees: [
    ['cypress', 0.62],
    ['olive', 0.23],
    ['pine', 0.15],
  ],
  townTrees: ['cypress'],
  bales: 'bale',
  walls: { kind: 'stone', stones: [mix('asphalt', 'cream', 0.3), mix('asphalt', 'cream', 0.5), mix('asphalt', 'grass', 0.25)] },
  wood: mix('brown', 'terracotta', 0.28),
  stand: { stripeA: PAL.red.clone(), stripeB: PAL.cream.clone(), banner: [PAL.cypress.clone(), PAL.cream.clone(), PAL.red.clone()] },
  tower: 'tuscan',
  hills: () => {
    const tint = (c: Color, t: number): Color => c.clone().lerp(TUSCAN_HORIZON, t);
    return [
      { radius: 4300, base: 60, amp: 560, color: tint(mix('asphalt', 'cream', 0.08), 0.26), haze: 0, shape: 'round' },
      { radius: 3400, base: 30, amp: 400, color: tint(mix('asphalt', 'olive', 0.4), 0.16), haze: 0, shape: 'round' },
      { radius: 2600, base: 0, amp: 250, color: tint(mix('grass', 'olive', 0.45), 0.12), haze: 0, shape: 'round' },
    ];
  },
};

/** Liguria, late morning: a harbour town under green hills, the sea on one side, a pale blue sky. */
const LIGURIA_HORIZON = hex('#e9e4d0');
const LIGURIA: Look = {
  id: 'liguria',
  name: 'Liguria',
  // A clear, high, almost white sun over an azure sky; green hills.
  sky: { horizon: LIGURIA_HORIZON, low: hex('#d9e2dc'), mid: hex('#b7d0d5'), high: hex('#92bbcc'), top: hex('#76a4bf'), sun: hex('#fbf6ea'), halo: hex('#eef0e6') },
  light: { sunElevationDeg: 34, lightElevationDeg: 36, sun: 0.8, hemi: 0.54, sunColor: '#fff6e6', skyColor: '#e4eeef', groundColor: '#9fa874', fogNear: 320, fogFar: 3900 },
  ground: { grass: hex('#a6b46a'), fieldA: hex('#8ca25c'), fieldB: hex('#c3b077'), fieldC: hex('#bcc68b') },
  trees: {
    cypress: PAL.cypress.clone(),
    canopy: hex('#3d5934'),
    leaf: hex('#7f8f57'),
    leafDark: hex('#697c4c'),
    bush: hex('#5f7a46'),
    bushDark: hex('#476440'),
    trunk: mix('brown', 'terracotta', 0.35),
  },
  houses: {
    // Portofino's: ochre, salmon, brick red, peach, a faded green.
    walls: [hex('#e2b45c'), hex('#dc9677'), hex('#c4623f'), hex('#eecaa1'), hex('#e8c27b'), hex('#b9b88c')],
    farm: [hex('#e2b45c'), hex('#dc9677')],
    roof: hex('#b85a3c'),
    shutter: hex('#3f6b4a'),
    door: hex('#5a4030'),
    tower: hex('#e8d2a8'),
    towerTop: hex('#ddc49a'),
    stone: hex('#b9ab95'),
    roofPitch: 0.24,
  },
  flora: { cypress: ['cypress'], pine: ['pine'], olive: ['olive'], bush: ['oleander', 'bush'] },
  farTrees: [
    ['cypress', 0.62],
    ['olive', 0.23],
    ['pine', 0.15],
  ],
  townTrees: ['palm', 'cypress'],
  bales: 'none',
  // Low plastered walls, washed pale.
  walls: { kind: 'stone', stones: [hex('#efe3c9'), hex('#e6d2ad'), hex('#dcc6a2')] },
  wood: hex('#7b5a42'),
  // A seaside awning: blue and white.
  stand: { stripeA: hex('#3f6f93'), stripeB: hex('#f3ead6'), banner: [hex('#3f6f93'), hex('#f3ead6'), hex('#c4623f')] },
  tower: 'striped',
  hills: ({ sea }) => {
    const tint = (c: Color, t: number): Color => c.clone().lerp(LIGURIA_HORIZON, t);
    const land = landArc(sea, 1.1, 0.5);
    return [
      // The Apennines behind, steep and blue-green; green hills in front, dropping to the sea at the headlands.
      { radius: 4300, base: 80, amp: 780, color: tint(hex('#7d9087'), 0.3), haze: 0, shape: 'round', arc: land },
      { radius: 3400, base: 40, amp: 560, color: tint(hex('#5f7a55'), 0.18), haze: 0, shape: 'round', arc: land },
      { radius: 2600, base: 0, amp: 330, color: tint(hex('#7c8c4f'), 0.1), haze: 0, shape: 'round', arc: landArc(sea, 1.25, 0.35) },
    ];
  },
  sea: { color: hex('#4f8c9b'), shallow: hex('#77aaa8'), level: -1.6 },
};

/** The Dolomites, early morning: pale peaks catching the sun, dark forest, green meadows, cool air. */
const DOLO_HORIZON = hex('#f2d8cd');
const DOLOMITES: Look = {
  id: 'dolomites',
  name: 'Dolomites',
  // Alpenglow: a pink horizon under a cool blue sky, crisp air (the peaks stay sharp far off).
  sky: { horizon: DOLO_HORIZON, low: hex('#eadad6'), mid: hex('#cfd8de'), high: hex('#a8c2d6'), top: hex('#89abca'), sun: hex('#fff6ea'), halo: hex('#f5e6dc') },
  light: { sunElevationDeg: 13, lightElevationDeg: 22, sun: 0.74, hemi: 0.56, sunColor: '#fff0e0', skyColor: '#dbe7f0', groundColor: '#93a07a', fogNear: 320, fogFar: 4300 },
  ground: { grass: hex('#99b862'), fieldA: hex('#83a856'), fieldB: hex('#b3c272'), fieldC: hex('#cfc489') },
  trees: {
    cypress: hex('#2b4434'),
    canopy: hex('#34503c'),
    // October's larches, gone gold.
    leaf: hex('#c6a24c'),
    leafDark: hex('#ab8a3f'),
    bush: hex('#5d7645'),
    bushDark: hex('#3f5a3c'),
    trunk: hex('#5b4232'),
  },
  houses: {
    // Chalets: whitewashed below, dark wood above (the walls carry the wood here), steep dark roofs.
    walls: [hex('#efe7d6'), hex('#8a5c3d'), hex('#e6dac4'), hex('#7a5038'), hex('#ddd0b6')],
    farm: [hex('#efe7d6'), hex('#8a5c3d')],
    roof: hex('#5e4c40'),
    shutter: hex('#a84a3a'),
    door: hex('#4a3427'),
    tower: hex('#e9e0cf'),
    towerTop: hex('#d9cfbd'),
    stone: hex('#a9a39a'),
    roofPitch: 0.5,
  },
  flora: { cypress: ['spruce'], pine: ['spruce', 'larch'], olive: ['larch'], bush: ['bush'] },
  farTrees: [
    ['spruce', 0.75],
    ['larch', 0.25],
  ],
  townTrees: ['spruce', 'larch'],
  bales: 'logs',
  // Wooden fences instead of stone walls, dark wood.
  walls: { kind: 'fence', stones: [hex('#a9a39a'), hex('#bcb6ac'), hex('#9a958d')] },
  wood: hex('#6b4a33'),
  stand: { stripeA: hex('#3f6b4a'), stripeB: hex('#f3ead6'), banner: [hex('#3f6b4a'), hex('#f3ead6'), hex('#a84a3a')] },
  tower: 'spire',
  hills: () => {
    const tint = (c: Color, t: number): Color => c.clone().lerp(DOLO_HORIZON, t);
    return [
      // The peaks: pale rock, pink in the morning, snow on the tops.
      { radius: 4300, base: 320, amp: 1550, color: tint(hex('#bba9a2'), 0.16), haze: 0, shape: 'jagged', snow: { color: hex('#f4efe9'), above: 0.56 }, segments: 1080 },
      { radius: 3400, base: 50, amp: 430, color: tint(hex('#3d5641'), 0.2), haze: 0, shape: 'round' },
      { radius: 2600, base: 0, amp: 230, color: tint(hex('#78904f'), 0.1), haze: 0, shape: 'round' },
    ];
  },
};

/** Sicily at dusk: golden wheat and dry earth, olives and prickly pears, Etna smoking on the skyline. */
const SICILY_HORIZON = hex('#f4bf86');
const SICILY: Look = {
  id: 'sicily',
  name: 'Sicily',
  sky: { horizon: SICILY_HORIZON, low: hex('#ee9f68'), mid: hex('#dc7f5c'), high: hex('#b5615e'), top: hex('#7d4f63'), sun: hex('#ffe3b0'), halo: hex('#f6bd85') },
  light: { sunElevationDeg: 8, lightElevationDeg: 21, sun: 0.72, hemi: 0.44, sunColor: '#ffbe82', skyColor: '#f0bb93', groundColor: '#b0603f', fogNear: 240, fogFar: 3300 },
  // Straw, darker wheat, ploughed red earth (plenty of it), pale stubble.
  ground: { grass: hex('#dba65a'), fieldA: hex('#c98f45'), fieldB: hex('#b0603f'), fieldC: hex('#e6c27a'), shares: [0.2, 0.26, 0.14] },
  trees: {
    cypress: PAL.cypress.clone(),
    canopy: hex('#3f5733'),
    leaf: hex('#7f8a52'),
    leafDark: hex('#6a7748'),
    bush: hex('#6c8a52'),
    bushDark: hex('#56743f'),
    trunk: mix('brown', 'terracotta', 0.35),
  },
  houses: {
    // Sandstone and whitewash, baroque ochre.
    walls: [hex('#e3c48f'), hex('#eee2cb'), hex('#d9a663'), hex('#e8d3a7')],
    farm: [hex('#eee2cb'), hex('#e3c48f')],
    roof: hex('#b85a3a'),
    shutter: hex('#41605a'),
    door: hex('#5a3d2c'),
    tower: hex('#dcb985'),
    towerTop: hex('#cfaa75'),
    stone: hex('#c4a983'),
    roofPitch: 0.2,
  },
  flora: { cypress: ['cypress', 'palm', 'cypress'], pine: ['pine'], olive: ['olive', 'citrus'], bush: ['cactus'] },
  farTrees: [
    ['cypress', 0.4],
    ['olive', 0.25],
    ['palm', 0.2],
    ['pine', 0.15],
  ],
  townTrees: ['palm', 'cypress'],
  bales: 'bale',
  // Pale limestone dry-stone walls.
  walls: { kind: 'stone', stones: [hex('#e6d6b2'), hex('#d8c49c'), hex('#cdb488')] },
  wood: hex('#7a5638'),
  // Sicily's own colours: yellow and red.
  stand: { stripeA: hex('#c9402f'), stripeB: hex('#e8b84a'), banner: [hex('#e8b84a'), hex('#f3e6c8'), hex('#c9402f')] },
  tower: 'dome',
  hills: ({ sunYaw }) => {
    const tint = (c: Color, t: number): Color => c.clone().lerp(SICILY_HORIZON, t);
    // Etna, well round from the sun: a broad cone, its top cut flat (the craters).
    const at = -(sunYaw + SICILY.volcano!.yawFromSun);
    const etna = (a: number): number => {
      const d = Math.abs(Math.atan2(Math.sin(a - at), Math.cos(a - at)));
      const x = d / 0.42;
      return x >= 1 ? 0 : Math.min(SICILY.volcano!.height * (1 - x) ** 1.35, SICILY.volcano!.height * 0.9);
    };
    return [
      { radius: 4300, base: 40, amp: 380, color: tint(hex('#8a6463'), 0.22), haze: 0, shape: 'round', bump: etna },
      { radius: 3400, base: 20, amp: 320, color: tint(hex('#a9785a'), 0.16), haze: 0, shape: 'round' },
      { radius: 2600, base: 0, amp: 220, color: tint(hex('#c99b55'), 0.1), haze: 0, shape: 'round' },
    ];
  },
  volcano: { color: hex('#8a6463'), smoke: hex('#dcc2b4'), height: 1250, yawFromSun: 1.9 },
};

export const LOOKS: Record<SettingId, Look> = { tuscany: TUSCANY, liguria: LIGURIA, dolomites: DOLOMITES, sicily: SICILY };

let current: Look = TUSCANY;
let currentSea: SeaPlan | null = null;

/** The setting the world is being built in (Tuscany unless told otherwise). */
export function look(): Look {
  return current;
}

/** The day's sea, if its setting has one (set while the world is built). */
export function seaPlan(): SeaPlan | null {
  return currentSea;
}

/** Build the next world in this setting (call before building it). */
export function useLook(id: SettingId, sea: SeaPlan | null = null): Look {
  current = LOOKS[id];
  currentSea = current.sea ? sea : null;
  return current;
}
